import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  InspectorControls,
  BlockControls,
} from '@wordpress/block-editor';
import {
  PanelBody,
  SelectControl,
  TextControl,
  ToolbarButton,
  Popover,
  Button,
} from '@wordpress/components';
import { useState } from '@wordpress/element';
import { link as linkIcon } from '@wordpress/icons';
import IconsModal from '../../components/IconsModal';
import {
  SERVICE_OPTIONS,
  getService,
  getIconClass,
  getServiceSvgPath,
} from '../SocialIcons/services';

/**
 * Editor for a single social link.
 *
 * The child stores data only — the parent's render.php owns every byte of
 * frontend markup (same split as Avatar / Avatar Group). Everything visual here
 * comes from parent context so the canvas preview matches the front end.
 */
export default function Edit({ attributes, setAttributes, context, isSelected }) {
  const { service, url, label, customIcon } = attributes;
  const [isUrlPopoverOpen, setIsUrlPopoverOpen] = useState(false);
  const [isIconModalOpen, setIsIconModalOpen] = useState(false);

  const size = context['brandy/socialIconSize'] || 20;
  const shape = context['brandy/socialIconShape'] || 'circle';
  const weight = context['brandy/socialIconWeight'] || 'fill';
  const colorMode = context['brandy/socialColorMode'] || 'brand';
  const showLabels = !!context['brandy/socialShowLabels'];

  const entry = getService(service);
  const isCustom = service === 'custom';
  const iconName = isCustom ? customIcon?.name || 'link' : entry.icon;
  const iconType = isCustom ? customIcon?.type || 'regular' : weight;
  const displayLabel = label || entry.label;
  // Services Phosphor does not cover bring their own mark; a custom-icon child
  // always uses the picker, so it never takes this path.
  const svgPath = isCustom ? '' : getServiceSvgPath(service);

  const blockProps = useBlockProps({
    className: `brandy-social-icons__item is-shape-${shape}${
      url ? '' : ' is-empty'
    }`,
    style:
      colorMode === 'brand' ? { '--brandy-social-brand': entry.color } : undefined,
  });

  return (
    <>
      <BlockControls>
        <ToolbarButton
          icon={linkIcon}
          label={__('Set profile URL', 'brandy-blocks')}
          onClick={() => setIsUrlPopoverOpen((open) => !open)}
          isPressed={isUrlPopoverOpen}
        />
      </BlockControls>

      <InspectorControls>
        <PanelBody title={__('Social link', 'brandy-blocks')}>
          <SelectControl
            label={__('Platform', 'brandy-blocks')}
            value={service}
            options={SERVICE_OPTIONS}
            onChange={(value) => setAttributes({ service: value })}
            __nextHasNoMarginBottom
          />

          {isCustom && (
            <>
              <Button
                variant="secondary"
                onClick={() => setIsIconModalOpen(true)}
                style={{ marginBottom: '16px' }}
              >
                {__('Choose icon', 'brandy-blocks')}
              </Button>
              <IconsModal
                isIconsModalOpen={isIconModalOpen}
                setIsIconsModalOpen={setIsIconModalOpen}
                icon={customIcon}
                setAttributes={(newIcon) =>
                  setAttributes({
                    customIcon: { ...customIcon, ...newIcon },
                  })
                }
              />
            </>
          )}

          <TextControl
            label={__('URL', 'brandy-blocks')}
            value={url}
            placeholder={entry.placeholder}
            onChange={(value) => setAttributes({ url: value })}
            help={
              service === 'email'
                ? __('An address without mailto: is linked automatically.', 'brandy-blocks')
                : service === 'phone'
                  ? __('A number without tel: is linked automatically.', 'brandy-blocks')
                  : undefined
            }
            __nextHasNoMarginBottom
          />

          <TextControl
            label={__('Label', 'brandy-blocks')}
            value={label}
            placeholder={entry.label}
            onChange={(value) => setAttributes({ label: value })}
            help={__(
              'Used as the accessible name, and as visible text when labels are on.',
              'brandy-blocks'
            )}
            __nextHasNoMarginBottom
          />
        </PanelBody>
      </InspectorControls>

      <li {...blockProps}>
        <span
          className="brandy-social-icons__link"
          style={{ '--brandy-social-icon-size': `${size}px` }}
        >
          {svgPath ? (
            <svg
              className="brandy-social-icons__svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              focusable="false"
            >
              <path d={svgPath} />
            </svg>
          ) : (
            <i className={getIconClass(iconName, iconType)} aria-hidden="true" />
          )}
          {showLabels && (
            <span className="brandy-social-icons__label">{displayLabel}</span>
          )}
        </span>

        {isSelected && isUrlPopoverOpen && (
          <Popover
            position="bottom center"
            onClose={() => setIsUrlPopoverOpen(false)}
            focusOnMount="firstElement"
          >
            <div style={{ padding: '12px', minWidth: '280px' }}>
              <TextControl
                label={displayLabel}
                value={url}
                placeholder={entry.placeholder}
                onChange={(value) => setAttributes({ url: value })}
                __nextHasNoMarginBottom
              />
            </div>
          </Popover>
        )}
      </li>
    </>
  );
}
